import { firestore } from '@src/api/Firebase/Firebase';
import { collection, doc } from 'firebase/firestore';
import { Input } from '@api/Inputs/Input';
import { getRecipeCost, getRecipeCostSale, Recipe } from '@api/Recipes/Recipe';
// Costeo por escenario (F7): necesita las opciones de cada modificador
import { getOptionConsumedCost, ModifierGroup, ModifierGroupOption } from '@api/ModifierGroups/ModifierGroup';

export interface DishComponent {
    recipeId: string;
    quantity: number; // recetas consumidas por producto
}

export interface DishModifierGroup {
    groupId: string;
    // Identidad del RENGLÓN dentro del producto: permite adjuntar el MISMO grupo
    // varias veces con etiquetas distintas ("Queso — Hamburguesa 1/2/3/4") en vez
    // de crear grupos casi-duplicados. La genera `saveDish` UNA vez y es estable —
    // las líneas de orden v2 la referencian como `groupInstanceId`.
    // AUSENTE = producto anterior a las instancias: su identidad sigue siendo el
    // groupId (comportamiento intacto).
    instanceId?: string;
    // Nombre del grupo EN ESTE producto (vacío = se usa el nombre del modificador).
    // Va al snapshot de texto de la línea → sale tal cual en el ticket y el tablero.
    label?: string;
    required: boolean;
    min: number; // selecciones mínimas (0 = opcional)
    max: number; // selecciones máximas (total, sumando cantidades)
    allowQuantity: boolean; // permite 2x de la misma opción
    // Máximo de opciones DISTINTAS elegibles (0/ausente = sin límite). Caso real:
    // "5 boneless con hasta 2 sabores" → max 5 (total) + maxDistinctOptions 2
    maxDistinctOptions?: number;
    // Liga el máximo TOTAL a las piezas elegidas en OTRO grupo del mismo producto
    // (''/ausente = usar `max` fijo). Caso real: "un sabor por pieza" — si eligen
    // "5 Boneless" el máximo de sabores es 5, si eligen "3 Tiras" es 3
    // (= Σ quantityPerSelection × cantidad de las opciones elegidas en ese grupo).
    // Guarda la CLAVE del renglón ligado (instanceId si lo tiene, groupId si no).
    maxFromGroupId?: string;
}

// Clave del renglón de grupo DENTRO del producto — es la identidad con la que se
// indexan selecciones, validación y modificadores. Productos previos a las
// instancias no traen instanceId: su clave sigue siendo el groupId.
// ⚠️ PARIDAD: replicado en krispis-web (src/api/Dishes/Dish.ts).
export const getDishGroupKey = (dishGroup: Pick<DishModifierGroup, 'groupId' | 'instanceId'>): string =>
    dishGroup.instanceId || dishGroup.groupId;

// Nombre a mostrar del grupo EN ESTE producto: la etiqueta capturada, o el nombre
// del modificador si no hay etiqueta. ⚠️ PARIDAD: replicado en krispis-web.
export const getDishGroupLabel = (
    dishGroup: Pick<DishModifierGroup, 'label'>,
    group?: { name: string }
): string => (dishGroup.label || '').trim() || group?.name || '';

export interface Dish {
    id?: string;
    name: string;
    cost: number; // PRECIO de venta — mismo nombre de campo que la legacy `products` y las líneas de orden (puntos intactos)
    productCategoryId: string; // reutiliza product-categories (funciona bien)
    photoSrc: string;
    description?: string;
    priority: number;
    status: boolean;
    availableOnline: boolean;
    branchOfficesAvailable: string[];
    components: DishComponent[];
    modifierGroups: DishModifierGroup[];
}

// Costo de insumos del producto (compra), costeado EN VIVO: Σ componentes (qty × costo vivo de la receta).
// Sin campo persistido a propósito — el plan solo persiste `cost` derivado en recetas.
export const getDishCost = (
    components: DishComponent[],
    recipesById: Map<string, Recipe>,
    inputsById: Map<string, Input>
): number => {
    return (components || []).reduce((total, component) => {
        const recipe = component.recipeId ? recipesById.get(component.recipeId) : undefined;
        if (!recipe) {
            return total;
        }
        return total + (Number(component.quantity) || 0) * getRecipeCost(recipe.items, inputsById);
    }, 0);
};

export const getDishCostSale = (
    components: DishComponent[],
    recipesById: Map<string, Recipe>,
    inputsById: Map<string, Input>
): number => {
    return (components || []).reduce((total, component) => {
        const recipe = component.recipeId ? recipesById.get(component.recipeId) : undefined;
        if (!recipe) {
            return total;
        }
        return total + (Number(component.quantity) || 0) * getRecipeCostSale(recipe.items, inputsById);
    }, 0);
};

// Margen sobre el precio de venta: (precio − costo) / precio
export const getDishMargin = (price: number, cost: number): number => {
    if (!Number.isFinite(price) || price <= 0) {
        return 0;
    }
    return (price - cost) / price;
};

export const formatMargin = (margin: number): string => `${(margin * 100).toFixed(1)}%`;

export const getDishMissingRecipeIds = (components: DishComponent[], recipesById: Map<string, Recipe>): string[] => {
    return (components || [])
        .map((component) => component.recipeId)
        .filter((recipeId) => !!recipeId && !recipesById.has(recipeId));
};

export const DISHES_COLLECTION = 'dishes';

export const dishesRef = collection(firestore, DISHES_COLLECTION);

// Id estable para renglones de grupo nuevos (auto-ID de Firestore, sin escribir
// nada) — mismo patrón que `generateModifierOptionId` de los modificadores
export const generateDishGroupInstanceId = (): string => doc(dishesRef).id;

// ============================================================================
// COSTO Y MARGEN POR ESCENARIO (F7) — funciones PURAS.
//
// El problema que resuelven: `getDishCost` solo suma los `components` (lo FIJO),
// así que un producto que consume todo por modificadores costeaba **$0 con 100%
// de margen**. Medido en el catálogo real: 15 de los 40 productos con
// modificadores mentían así — el Combo MEGA decía "100%" cuando su realidad
// está entre 46% y 63%.
//
// Un producto configurable no tiene UN costo, tiene un RANGO:
//   - **Mejor caso**: cumplir apenas los mínimos, con las opciones más baratas.
//   - **Peor caso**: llenar hasta el máximo efectivo, con las más caras.
//
// Dos decisiones que evitan cambiar un número falso por otro:
//   ① **El precio del escenario incluye el `extraCost` de las opciones elegidas.**
//      Un extra cobrado también es INGRESO: sin esto los Cheese Jalapeños darían
//      −277% de margen en vez de 21%.
//   ② **Los productos de regalo** (precio ~0: HAMBURGUESA GRATIS, Bebida de
//      cumpleaños) devuelven el costo en $ pero **sin porcentaje**: un −4,727% no
//      informa nada. Se detectan por precio, no por nombre.
// ============================================================================

export interface DishCostScenario {
    /** Costo de insumos del escenario (componentes + opciones elegidas). */
    cost: number;
    /** Precio real del escenario = precio del producto + extras cobrados. */
    price: number;
    /** (precio − costo) / precio. `undefined` en productos de regalo. */
    margin?: number;
}

export interface DishCostScenarios {
    best: DishCostScenario;
    worst: DishCostScenario;
    /** true = producto de regalo/cortesía: se muestra costo, no porcentaje. */
    isGiveaway: boolean;
    /** true = los dos escenarios coinciden (producto sin nada elegible). */
    isFixed: boolean;
}

/** Precio por debajo del cual un producto es regalo/cortesía y el % no informa. */
const GIVEAWAY_PRICE_THRESHOLD = 1;

/**
 * Máximo efectivo de un renglón cuando NADIE ha elegido todavía (costeo, no venta).
 * Con `maxFromGroupId` el tope depende de las piezas del renglón ligado: para el
 * PEOR caso se asume que se eligieron las piezas máximas posibles de ese renglón.
 */
const getScenarioMax = (
    dishGroup: DishModifierGroup,
    dishGroups: DishModifierGroup[],
    modifierGroupsById: Map<string, ModifierGroup>
): number => {
    if (!dishGroup.maxFromGroupId) {
        return Number(dishGroup.max) || 0;
    }
    const linkedRow =
        dishGroups.find((row) => getDishGroupKey(row) === dishGroup.maxFromGroupId) ||
        dishGroups.find((row) => row.groupId === dishGroup.maxFromGroupId);
    if (!linkedRow) {
        return Number(dishGroup.max) || 0; // referencia rota: máximo fijo
    }
    const linkedGroup = modifierGroupsById.get(linkedRow.groupId);
    if (!linkedGroup) {
        return Number(dishGroup.max) || 0;
    }
    // Piezas máximas que ese renglón puede aportar: su tope de selecciones por la
    // opción que más piezas da por selección
    const maxPiecesPerSelection = (linkedGroup.options || []).reduce(
        (max: number, option: ModifierGroupOption) => Math.max(max, Number(option.quantityPerSelection) || 0),
        0
    );
    return (Number(linkedRow.max) || 0) * maxPiecesPerSelection;
};

/**
 * Costo y precio extra de un renglón en un escenario.
 * `pick: 'cheapest' | 'priciest'` decide qué opción se elige; `selections` cuántas.
 */
const getRowScenario = (
    dishGroup: DishModifierGroup,
    dishGroups: DishModifierGroup[],
    modifierGroupsById: Map<string, ModifierGroup>,
    recipesById: Map<string, Recipe>,
    inputsById: Map<string, Input>,
    pick: 'cheapest' | 'priciest'
): { cost: number; extraPrice: number } => {
    const group = modifierGroupsById.get(dishGroup.groupId);
    const options = (group?.options || []).filter((option: ModifierGroupOption) => option.status !== false);
    if (options.length === 0) {
        return { cost: 0, extraPrice: 0 };
    }

    const selections =
        pick === 'cheapest'
            ? // Mejor caso: solo lo obligatorio (required sin min explícito = 1)
              Math.max(Number(dishGroup.min) || 0, dishGroup.required && !dishGroup.min ? 1 : 0)
            : getScenarioMax(dishGroup, dishGroups, modifierGroupsById);

    if (selections <= 0) {
        return { cost: 0, extraPrice: 0 };
    }

    // El costo de una selección = receta × quantityPerSelection; el ingreso = extraCost
    const costed = options.map((option: ModifierGroupOption) => ({
        option,
        cost: getOptionConsumedCost(option, recipesById, inputsById),
        extraCost: Number(option.extraCost) || 0
    }));
    // Se ordena por COSTO de insumos, que es lo que mueve el margen por el lado caro
    costed.sort((a, b) => a.cost - b.cost);

    // ⚠️ `allowQuantity: false` significa que NO se puede repetir la misma opción:
    // el escenario debe tomar N opciones DISTINTAS, no N veces la misma. Ignorarlo
    // inflaba el peor caso y generaba falsas alarmas de margen negativo (lo delató
    // Cheese Jalapeños: 4 × la salsa más cara es una combinación imposible).
    const canRepeat = dishGroup.allowQuantity === true;
    const ordered = pick === 'cheapest' ? costed : [...costed].reverse();

    if (canRepeat) {
        const chosen = ordered[0];
        return { cost: chosen.cost * selections, extraPrice: chosen.extraCost * selections };
    }

    // Sin repetición no se pueden elegir más opciones de las que existen
    const distinct = ordered.slice(0, Math.min(selections, ordered.length));
    return {
        cost: distinct.reduce((total, item) => total + item.cost, 0),
        extraPrice: distinct.reduce((total, item) => total + item.extraCost, 0)
    };
};

/**
 * Rango de costo y margen de un producto: mejor y peor caso.
 * Sin modificadores elegibles ambos escenarios coinciden (`isFixed`).
 */
export const getDishCostScenarios = (
    dish: Pick<Dish, 'cost' | 'components' | 'modifierGroups'>,
    modifierGroupsById: Map<string, ModifierGroup>,
    recipesById: Map<string, Recipe>,
    inputsById: Map<string, Input>
): DishCostScenarios => {
    const basePrice = Number(dish.cost) || 0;
    const baseCost = getDishCost(dish.components || [], recipesById, inputsById);
    const dishGroups = dish.modifierGroups || [];

    const build = (pick: 'cheapest' | 'priciest'): DishCostScenario => {
        let cost = baseCost;
        let extraPrice = 0;
        dishGroups.forEach((dishGroup) => {
            const row = getRowScenario(dishGroup, dishGroups, modifierGroupsById, recipesById, inputsById, pick);
            cost += row.cost;
            extraPrice += row.extraPrice;
        });
        const price = basePrice + extraPrice;
        return {
            cost,
            price,
            // Regalo: el costo informa, el porcentaje no
            margin: price > GIVEAWAY_PRICE_THRESHOLD ? (price - cost) / price : undefined
        };
    };

    const best = build('cheapest');
    const worst = build('priciest');

    return {
        best,
        worst,
        isGiveaway: basePrice <= GIVEAWAY_PRICE_THRESHOLD,
        isFixed: best.cost === worst.cost && best.price === worst.price
    };
};
